import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { useRouter } from "@miplatform/hooks";
import { Grid } from "@mui/material";
import { AppSideNav } from "../../components/AppSideNav/AppSideNav";
import { ErrorBoundary } from "../../components/ErrorBoundary/ErrorBoundary";
import { ErrorPage } from "../../components/ErrorPage/ErrorPage";
import { MiAppBar } from "../../components/MiAppBar/MiAppBar";
import { useProfile, useSession } from "../../hooks";
import { usePageTitle } from "./usePageTitle";

/**
 *
 * @param {Object} props
 * @param {Array} props.sideNavItems - Array of side navigation items to display
 * @param {boolean} [props.isError] - Flag indicating if there is an error to display the error page
 * @param {string} [props.appBarColor] - Optional background color for the app bar
 * @param {React.ReactNode} [props.appLogo] - Optional custom logo component to display in the app bar
 * @param {boolean} [props.hideSearchBar] - Flag to hide the search bar
 * @param {function} [props.refreshOnNavigate] - Optional function to refresh data on navigation
 * @returns
 */
export function MainLayout({
  sideNavItems,
  isError,
  appBarColor,
  appLogo,
  hideSearchBar,
  refreshOnNavigate,
}) {
  const { classes } = useStyles();

  const { getProfile, profile } = useProfile();
  const { session } = useSession();
  const { location } = useRouter();

  const userId = session?.userId;

  usePageTitle();
  useEffect(() => {
    if (userId && !profile) {
      getProfile(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (refreshOnNavigate) {
      refreshOnNavigate();
    }
  }, [location.pathname]);

  const renderContent = () => {
    if (isError) {
      return <ErrorPage />;
    }

    const content = <Outlet />;

    return process.env.NODE_ENV === "development" ? (
      content
    ) : (
      <ErrorBoundary>{content}</ErrorBoundary>
    );
  };

  return (
    <Grid container direction="column" className={classes.layoutRoot}>
      <Grid>
        <MiAppBar
          tenantName={profile?.tenantName}
          appBarColor={appBarColor}
          appLogo={appLogo}
          hideSearchBar={hideSearchBar}
        />
      </Grid>
      <Grid className={classes.pageContainer}>
        {sideNavItems && (
          <Grid item className={classes.sideNavContainer}>
            <AppSideNav navItems={sideNavItems} />
          </Grid>
        )}
        <Grid item className={classes.mainContent}>
          <Grid className={classes.outletContainer}>{renderContent()}</Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

const useStyles = makeStyles()((theme) => ({
  layoutRoot: {
    height: "100vh",
    flexWrap: "nowrap",
    overflow: "hidden",
  },
  pageContainer: {
    display: "flex",
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    height: "100%",
  },
  sideNavContainer: {
    minHeight: 0,
    position: "sticky",
    top: 0,
    height: "100%",
    overflow: "hidden",
    zIndex: theme.zIndex.sideNav,
  },
  mainContent: {
    flex: "1 1 0%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  outletContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    overflowY: "auto",
  },
}));
